import { attachClientTelemetry } from 'Shared/network/telemetryInterceptor';
import { type QueryState, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import type { GetServerStatusResponse } from 'viewup-api/v2';

// `Shared/network` 의 httpClient 를 쓰지 마라 — auth gate 가 unauthorized 상태에서 요청 자체를 throw 하고,
// 앱 QueryProvider 가 그 에러를 홈 리다이렉트로 처리한다. 점검 판정은 토큰과 무관해야 하므로
// 토큰·credentials 없는 별도 raw 인스턴스를 쓴다.
// timeout 이 없으면 장애 중 연결이 매달릴 때 쿼리가 영영 pending 이라 점검 화면이 뜨지 않는다.
const healthAxios = axios.create({
  baseURL: process.env.NEXT_PUBLIC_VIEWUP_API_URL,
  timeout: 5_000,
});
// 요청을 내보내는 transport 는 계측 필수 (shared/CLAUDE.md). 비활성/Worker/SSR 환경에선 no-op.
attachClientTelemetry(healthAxios);

const HEALTH_PATH = '/v2';
const HEALTH_OK = 'ok';
const RETRY_COUNT = 2;
const RETRY_DELAY_MS = 300;
const RECOVERY_POLL_INTERVAL_MS = 30_000;

// 도메인 key factory 가 아니라 이 게이트 전용 단일 쿼리라 `shared/const/queryKey.ts` 에 등록하지 않는다.
const HEALTH_CHECK_QUERY_KEY = ['maintenance', 'health'] as const;

interface HealthCheckResult {
  isOk: boolean;
}

const fetchHealth = async (): Promise<HealthCheckResult> => {
  const { data } = await healthAxios.get<GetServerStatusResponse>(HEALTH_PATH);
  return { isOk: data?.data === HEALTH_OK };
};

// 응답이 와서 ok 가 아니면 항상 점검. 에러(5xx·네트워크·timeout)는 ok 를 한 번도 못 받았을 때만 점검이다 —
// 이미 ok 를 받은 뒤의 refetch 실패(네트워크 전환 시 refetchOnReconnect 등)로 앱 전체를 점검 화면으로
// 뒤집으면 children 이 언마운트되어 입력·스크롤 상태가 날아간다.
const isNotOkState = ({
  status,
  data,
}: Pick<QueryState<HealthCheckResult, Error>, 'status' | 'data'>) =>
  data?.isOk === false || (status === 'error' && data === undefined);

/**
 * 헬스체크 `GET {API}/v2` 결과. `isNotOk` 는 응답이 ok 가 아니거나, ok 를 받은 적 없이 에러(5xx·네트워크 포함)일 때 true —
 * 응답 전(pending)에는 false 라 children 을 막지 않는다.
 * 점검 상태일 때만 30초마다 다시 물어 자동 복구한다 (정상일 땐 폴링 없음).
 */
export const useHealthCheck = () => {
  const { status, data } = useQuery({
    queryFn: fetchHealth,
    queryKey: HEALTH_CHECK_QUERY_KEY,
    refetchInterval: (query) => (isNotOkState(query.state) ? RECOVERY_POLL_INTERVAL_MS : false),
    retry: RETRY_COUNT,
    retryDelay: RETRY_DELAY_MS,
  });

  return { isNotOk: isNotOkState({ status, data }) };
};
